import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CheckCircle, Clock, ChefHat, Package, MapPin, Phone, XCircle, RefreshCw } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";

const TrackOrderPage = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [orderNumber]);

  const fetchOrder = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/orders/${orderNumber}`);
      setOrder(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching order:", error);
      setError("Bestilling ikke funnet");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { 
        icon: <Clock className="h-16 w-16" />, 
        label: "Venter på betaling", 
        labelEn: "Pending Payment",
        color: "#9ca3af",
        bgColor: "#f3f4f6",
        message: "Fullfør betalingen for å sende bestillingen."
      },
      paid: { 
        icon: <CheckCircle className="h-16 w-16" />, 
        label: "Bestilling mottatt", 
        labelEn: "Order Received",
        color: "#22c55e",
        bgColor: "#dcfce7",
        message: "Restauranten behandler din bestilling nå..."
      },
      confirmed: { 
        icon: <ChefHat className="h-16 w-16" />, 
        label: "Bekreftet - Tilberedes", 
        labelEn: "Confirmed - Preparing",
        color: "#f59e0b",
        bgColor: "#fef3c7",
        message: "Maten din tilberedes nå!"
      },
      preparing: { 
        icon: <ChefHat className="h-16 w-16" />, 
        label: "Tilberedes", 
        labelEn: "Being Prepared",
        color: "#f59e0b",
        bgColor: "#fef3c7",
        message: "Maten din tilberedes nå!"
      },
      ready: { 
        icon: <Package className="h-16 w-16" />, 
        label: "Klar for henting!", 
        labelEn: "Ready for Pickup!",
        color: "#3b82f6",
        bgColor: "#dbeafe",
        message: "Kom og hent din bestilling!"
      },
      completed: { 
        icon: <CheckCircle className="h-16 w-16" />, 
        label: "Fullført", 
        labelEn: "Completed",
        color: "#22c55e",
        bgColor: "#dcfce7",
        message: "Takk for din bestilling!"
      },
      cancelled: { 
        icon: <XCircle className="h-16 w-16" />, 
        label: "Avvist", 
        labelEn: "Cancelled",
        color: "#ef4444",
        bgColor: "#fee2e2",
        message: "Beklager, denne bestillingen ble avvist. Kontakt oss for mer informasjon."
      },
      declined: { 
        icon: <XCircle className="h-16 w-16" />, 
        label: "Avvist", 
        labelEn: "Declined",
        color: "#ef4444",
        bgColor: "#fee2e2",
        message: "Beklager, denne bestillingen ble avvist. Kontakt oss for mer informasjon."
      },
      awaiting_payment: { 
        icon: <Clock className="h-16 w-16" />, 
        label: "Venter på betaling", 
        labelEn: "Awaiting Payment",
        color: "#9ca3af",
        bgColor: "#f3f4f6",
        message: "Fullfør betalingen for å sende bestillingen."
      },
    };
    return statuses[status] || statuses.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
        <Navbar />
        <div className="flex items-center justify-center h-96" data-testid="track-order-loading">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-t-transparent" style={{ borderColor: '#721111', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#4a3728' }}>Laster bestilling...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
        <Navbar />
        <div className="flex items-center justify-center h-96" data-testid="track-order-error">
          <div className="text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
            <p className="text-xl font-semibold" style={{ color: '#2d1810' }}>{error}</p>
            <p className="mt-2" style={{ color: '#4a3728' }}>Sjekk at ordrenummeret er korrekt.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-8"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-serif-display font-bold mb-2" style={{ color: '#d4af37' }}>
            Spor Din Bestilling
          </h1>
          <p style={{ color: '#f5e6d3' }}>Bestilling #{order.order_number}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-6"
          style={{ border: '3px solid #d4af37' }}
          data-testid="order-status-card"
        >
          {/* Refresh Button */}
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => fetchOrder(true)}
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Oppdater
            </Button>
          </div>

          {/* Status Icon and Label */}
          <div 
            className="text-center p-8 rounded-xl mb-6"
            style={{ background: statusInfo.bgColor }}
          >
            <div className="flex justify-center mb-4" style={{ color: statusInfo.color }}>
              {statusInfo.icon}
            </div>
            <h2 
              className="text-2xl sm:text-3xl font-serif-display font-bold mb-2"
              style={{ color: statusInfo.color }}
              data-testid="order-status-label"
            >
              {statusInfo.label}
            </h2>
            <p style={{ color: '#4a3728' }}>{statusInfo.message}</p>
          </div>

          {/* Estimated Time - Show when confirmed */}
          {(order.status === 'confirmed' || order.status === 'preparing') && order.estimated_minutes && (
            <div 
              className="text-center p-6 rounded-xl mb-6"
              style={{ background: '#dcfce7', border: '2px solid #22c55e' }}
              data-testid="estimated-time-box"
            >
              <p className="font-semibold mb-2" style={{ color: '#166534' }}>Estimert tid til henting:</p>
              <p className="text-4xl font-bold" style={{ color: '#15803d' }}>
                ca. {order.estimated_minutes} minutter
              </p>
              {order.confirmed_at && (
                <p className="text-sm mt-2" style={{ color: '#166534' }}>
                  Bekreftet kl. {new Date(order.confirmed_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          )}

          {/* Customer's requested pickup time */}
          {order.pickup_time && (
            <div 
              className="text-center p-4 rounded-xl mb-6"
              style={{ background: '#f4e7d8' }}
            >
              <p className="text-sm" style={{ color: '#8b4513' }}>Din ønskede hentetid:</p>
              <p className="text-xl font-bold" style={{ color: '#721111' }}>Kl. {order.pickup_time}</p>
            </div>
          )}

          {/* Pickup Location */}
          <div 
            className="p-4 rounded-xl mb-6"
            style={{ background: '#f4e7d8' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-5 w-5" style={{ color: '#721111' }} />
              <p className="font-semibold" style={{ color: '#2d1810' }}>Hent bestillingen her:</p>
            </div>
            <p style={{ color: '#4a3728' }}>Rikard Kaarbøs plass 1, 9405 Harstad</p>
            <div className="flex items-center gap-2 mt-2">
              <Phone className="h-4 w-4" style={{ color: '#721111' }} />
              <a href="tel:+4748915190" className="hover:underline" style={{ color: '#721111' }}>
                +47 48 91 51 90
              </a>
            </div>
          </div>

          {/* Order Items */}
          <div className="pt-6" style={{ borderTop: '2px solid #d4af37' }}>
            <h3 className="text-lg font-serif-display font-semibold mb-4" style={{ color: '#2d1810' }}>
              Din bestilling
            </h3>
            <div className="space-y-3">
              {order.line_items.map((item, index) => (
                <div 
                  key={index} 
                  className="flex justify-between items-center p-3 rounded-lg"
                  style={{ background: '#f4e7d8' }}
                  data-testid={`order-item-${index}`}
                >
                  <div>
                    <span className="font-medium" style={{ color: '#2d1810' }}>{item.name}</span>
                    <span className="text-sm ml-2" style={{ color: '#8b4513' }}>x{item.quantity}</span>
                    {item.meat_choice && (
                      <span className="text-sm ml-2" style={{ color: '#721111' }}>({item.meat_choice})</span>
                    )}
                  </div>
                  <span className="font-semibold" style={{ color: '#721111' }}>kr {item.total_price},-</span>
                </div>
              ))}
            </div>

            {/* Notes */}
            {order.customer_notes && (
              <div className="mt-4 p-3 rounded-lg" style={{ background: '#fef3c7' }}>
                <p className="text-sm font-semibold" style={{ color: '#92400e' }}>Notater:</p>
                <p className="text-sm" style={{ color: '#92400e' }}>{order.customer_notes}</p>
              </div>
            )}

            {/* Total */}
            <div className="mt-6 pt-4" style={{ borderTop: '2px solid #d4af37' }}>
              {order.coupon_discount > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span style={{ color: '#4a3728' }}>Rabatt</span>
                  <span style={{ color: '#22c55e' }}>-kr {order.coupon_discount},-</span>
                </div>
              )}
              {order.gift_card_discount > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span style={{ color: '#4a3728' }}>Gavekort</span>
                  <span style={{ color: '#22c55e' }}>-kr {order.gift_card_discount},-</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold">
                <span style={{ color: '#2d1810' }}>Total</span>
                <span style={{ color: '#721111' }} data-testid="order-total">kr {order.total_amount},-</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Auto-refresh notice */}
        <p className="text-center text-sm" style={{ color: '#8b4513' }}>
          Siden oppdateres automatisk hvert 10. sekund
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default TrackOrderPage;
